import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  cancelableResource,
  cancelableRxResource,
  debouncedSignal,
} from '@softline/core';
import { PhotonService, PhotonSuggestion } from '@softapps/core';
import {
  ButtonComponent,
  IconComponent,
  InputComponent,
  LoadingSpinnerComponent,
  MultilineInputComponent,
  ResolvableModal,
  SearchInputComponent,
  SoftSlotDirective,
} from '@softline/ui-core';
import { ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import { DateInputComponent } from '@softline/ui-core/date-input';
import { SelectDsComponent, SelectDsItem } from '@softline/ui-core/select';
import {
  CONSTRUCTION_SITE_LOOKUP_PAGE_SIZE,
  ConstructionSiteLookupRepository,
} from '../../repositories/construction-site-lookup.repository';
import { ConstructionSiteFormService } from '../../services/construction-site-form.service';
import { ConstructionSiteFormValue } from '../../types/construction-site-form';

const LOOKUP_SEARCH_DEBOUNCE_MS = 300;

/**
 * Create / edit form for a construction site (Baustelle) of an invoicing customer.
 *
 * Two usages: as a modal it resolves with the site's `customerId` — usable
 * wherever a customer id is expected (e.g. as the delivery customer of a sales
 * document) — or `null` when cancelled. With `embedded` it renders inside a host
 * that already owns the modal chrome (the Beleg wizard): the own action row and
 * the modal bottom outlet are suppressed, the host drives {@link save} and
 * listens to {@link saved}.
 *
 * `bankId` / `bankAccount` are not offered: the masterdata service has no bank
 * lookup yet, so a bank could only be entered as an unvalidated raw id.
 */
@Component({
  selector: 'soft-construction-site-form',
  templateUrl: './construction-site-form.component.html',
  styleUrl: './construction-site-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    DateInputComponent,
    MultilineInputComponent,
    ProgressStepsComponent,
    SelectDsComponent,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    LoadingSpinnerComponent,
    SearchInputComponent,
  ],
  providers: [ConstructionSiteFormService],
  host: { '[class.embedded]': 'embedded()' },
})
export class ConstructionSiteFormComponent extends ResolvableModal<string | null> {
  private readonly formService = inject(ConstructionSiteFormService);
  private readonly lookupRepository = inject(ConstructionSiteLookupRepository);
  private readonly photonService = inject(PhotonService);

  /** Invoicing customer the site belongs to. */
  readonly customerId = input<string | null>(null);
  /** Site to edit; `null` creates a new one. */
  readonly constructionSiteId = input<string | null>(null);
  /** Render without the own action row, for a host that already has one. */
  readonly embedded = input<boolean, unknown>(false, { transform: booleanAttribute });

  /** The saved site's `customerId`. The only success channel while `embedded` —
   *  resolving would close the HOST's modal. */
  readonly saved = output<string>();
  readonly cancelled = output<void>();

  private readonly bottomTpl = viewChild('bottomTpl', { read: TemplateRef });
  override readonly bottomTemplate = computed<TemplateRef<unknown> | null>(() =>
    this.embedded() ? null : (this.bottomTpl() ?? null)
  );

  protected readonly value = this.formService.value;
  protected readonly mode = this.formService.mode;
  protected readonly isLoading = this.formService.isLoading;
  readonly isSaving = this.formService.isSaving;
  protected readonly isReadonly = this.formService.isReadonly;
  protected readonly errorMessage = this.formService.errorMessage;
  readonly canSave = this.formService.canSave;

  private readonly syncParams = effect(() => {
    this.formService.customerId.set(this.customerId());
    this.formService.constructionSiteId.set(this.constructionSiteId());
  });

  protected readonly representativeSearch = signal('');
  private readonly debouncedRepresentativeSearch = debouncedSignal(
    this.representativeSearch,
    LOOKUP_SEARCH_DEBOUNCE_MS
  );
  /** A failed lookup renders as an empty list — a resource left in the error
      state throws on every value() read and freezes the whole dialog. */
  private readonly representativesResource = cancelableResource({
    params: () => ({ term: this.debouncedRepresentativeSearch() }),
    loader: ({ params }) =>
      this.lookupRepository
        .searchRepresentatives(params.term, {
          limit: CONSTRUCTION_SITE_LOOKUP_PAGE_SIZE,
        })
        .catch(() => ({ items: [], totalCount: 0 })),
  });
  protected readonly representativeItems = computed<SelectDsItem<string>[]>(() =>
    (this.representativesResource.value()?.items ?? []).map((r) => ({
      value: r.id,
      label: r.name ? `${r.key} - ${r.name}` : r.key,
    }))
  );
  protected readonly representativesLoading = computed(
    () => this.representativesResource.status() === 'loading'
  );

  protected readonly countrySearch = signal('');
  private readonly debouncedCountrySearch = debouncedSignal(
    this.countrySearch,
    LOOKUP_SEARCH_DEBOUNCE_MS
  );
  private readonly countriesResource = cancelableResource({
    params: () => ({ term: this.debouncedCountrySearch() }),
    loader: ({ params }) =>
      this.lookupRepository
        .searchCountries(params.term, {
          limit: CONSTRUCTION_SITE_LOOKUP_PAGE_SIZE,
        })
        .catch(() => ({ items: [], totalCount: 0 })),
  });
  protected readonly countryItems = computed<SelectDsItem<string>[]>(() =>
    (this.countriesResource.value()?.items ?? []).map((c) => ({
      value: c.key,
      label: c.name ? `${c.key} - ${c.name}` : c.key,
    }))
  );
  protected readonly countriesLoading = computed(
    () => this.countriesResource.status() === 'loading'
  );

  protected readonly addressSearch = signal('');
  private readonly debouncedAddressSearch = debouncedSignal(
    this.addressSearch,
    LOOKUP_SEARCH_DEBOUNCE_MS
  );
  private readonly addressSuggestionsResource = cancelableRxResource({
    params: () => ({ query: this.debouncedAddressSearch() }),
    stream: ({ params }) => this.photonService.search(params.query),
  });
  protected readonly addressSuggestions = computed<PhotonSuggestion[]>(
    () => this.addressSuggestionsResource.value() ?? []
  );
  protected readonly addressSuggestionsLoading = computed(
    () => this.addressSuggestionsResource.status() === 'loading'
  );
  protected readonly addressSuggestionsOpen = computed(
    () => !this.isReadonly() && this.addressSearch().trim().length >= 3
  );

  /** Fills the structured address fields from a picked suggestion — the backend
   *  validates street / PLZ / city separately, a one-line address is useless. */
  protected pickAddressSuggestion(suggestion: PhotonSuggestion): void {
    this.formService.set('street', suggestion.street);
    this.formService.set('streetNumber', suggestion.housenumber);
    this.formService.set('postCode', suggestion.postcode);
    this.formService.set('city', suggestion.city);
    if (suggestion.countryCode) {
      this.formService.set('countryCode', suggestion.countryCode);
    }
    this.addressSearch.set('');
  }

  protected readonly title = computed(() =>
    this.mode() === 'create' ? 'Baustelle erstellen' : 'Baustelle ändern'
  );

  protected readonly stepIndex = signal(0);
  protected readonly extrasOpen = signal(false);

  /** Freely navigable — required data is flagged in the summary, never gated. */
  protected readonly steps: ProgressStep[] = [
    {
      header: 'Stammdaten',
      subHeader: 'Bezeichnung, Adresse',
      execute: () => this.stepIndex.set(0),
    },
    {
      header: 'Kontakt',
      subHeader: 'Vertreter, Sachbearbeiter',
      execute: () => this.stepIndex.set(1),
    },
    {
      header: 'Lieferhinweise',
      subHeader: 'Optional',
      execute: () => this.stepIndex.set(2),
    },
  ];

  protected readonly stepHint = computed(() => {
    const sections = ['Stammdaten', 'Kontakt', 'Lieferhinweise'];
    return `Schritt ${this.stepIndex() + 1} von 3 · ${sections[this.stepIndex()]}`;
  });

  protected readonly missingSummary = computed(() =>
    this.value().name1.trim()
      ? 'Alle Pflichtfelder sind befüllt.'
      : 'Name 1 fehlt noch (Schritt 1 · Stammdaten).'
  );

  protected goToStep(index: number): void {
    this.stepIndex.set(index);
  }

  protected onPrimary(): void {
    if (this.stepIndex() < 2) this.stepIndex.update((i) => i + 1);
    else void this.save();
  }

  protected set(field: keyof ConstructionSiteFormValue, fieldValue: unknown): void {
    this.formService.set(field, fieldValue == null ? '' : String(fieldValue));
  }

  /** Public so an embedding host's own action row can drive it. */
  async save(): Promise<string | null> {
    const customerId = await this.formService.save();
    if (!customerId) return null;
    this.saved.emit(customerId);
    if (!this.embedded()) this.resolve(customerId);
    return customerId;
  }

  protected onCancel(): void {
    this.cancelled.emit();
    if (!this.embedded()) this.resolve(null);
  }
}
